import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePasswordReset } from '../../hooks/usePasswordReset';

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    newPassword,
    setNewPassword,
    confirmPassword,
    setConfirmPassword,
    isResetting,
    tokenValid,
    isValidating,
    errors,
    resetPassword,
  } = usePasswordReset();

  // Password requirement checks (real-time)
  const hasLength = newPassword.length >= 8;
  const hasUppercase = /[A-Z]/.test(newPassword);
  const hasNumber = /[0-9]/.test(newPassword);
  const passwordsMatch = newPassword.length > 0 && confirmPassword.length > 0 && newPassword === confirmPassword;

  const strengthScore = [hasLength, hasUppercase, hasNumber].filter(Boolean).length;
  const strengthLabel =
    strengthScore === 0 ? null :
    strengthScore === 1 ? t('auth.resetPassword.strengthWeak') :
    strengthScore === 2 ? t('auth.resetPassword.strengthMedium') :
    t('auth.resetPassword.strengthStrong');
  const strengthColor =
    strengthScore <= 1 ? 'bg-red-500' :
    strengthScore === 2 ? 'bg-yellow-500' :
    'bg-green-500';
  const strengthTextColor =
    strengthScore <= 1 ? 'text-red-400' :
    strengthScore === 2 ? 'text-yellow-400' :
    'text-green-400';

  // --- Validating State ---
  if (isValidating) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="relative h-16 w-16 mx-auto mb-4">
            <div className="absolute inset-0 rounded-full border-4 border-gray-800" />
            <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-yellow-500 border-r-yellow-400 animate-spin" />
          </div>
          <p className="text-gray-400 text-sm">{t('auth.resetPassword.validating')}</p>
        </div>
      </div>
    );
  }

  // --- Invalid Token State ---
  if (tokenValid === false) {
    return (
      <div className="min-h-screen bg-black text-white">
        <header className="sticky top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-md border-b border-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center h-14 sm:h-16">
              <Link to="/" className="flex items-center">
                <span className="text-xl sm:text-2xl font-bold">
                  <span className="text-yellow-400">Nutri</span>
                  <span className="text-white">Bot</span>
                </span>
              </Link>
            </div>
          </div>
        </header>

        <div className="min-h-[calc(100vh-56px)] sm:min-h-[calc(100vh-64px)] flex items-center justify-center px-4 py-8">
          <div className="max-w-md w-full">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-red-500/20 rounded-2xl flex items-center justify-center">
                <i className="ri-error-warning-line text-red-400 text-3xl"></i>
              </div>
            </div>

            <div className="text-center mb-6 sm:mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold mb-3 text-red-400">
                {t('auth.resetPassword.invalidToken')}
              </h1>
              <p className="text-gray-300 text-sm sm:text-base">
                {t('auth.resetPassword.invalidTokenMessage')}
              </p>
            </div>

            <div className="bg-gray-900 rounded-2xl p-6 sm:p-8 border border-gray-800 space-y-3">
              <Link
                to="/forgot-password"
                className="flex items-center justify-center gap-2 w-full py-3 sm:py-4 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold rounded-xl hover:from-yellow-500 hover:to-yellow-700 transition-ball duration-200 hover:scale-105 shadow-lg"
              >
                <i className="ri-mail-send-line text-lg"></i>
                {t('auth.resetPassword.requestNewLink')}
              </Link>

              <Link
                to="/login"
                className="flex items-center justify-center gap-2 w-full py-3 text-gray-400 hover:text-yellow-400 font-medium text-sm transition-colors"
              >
                <i className="ri-arrow-left-line"></i>
                {t('auth.resetPassword.backToLogin')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- Valid Token: Show Reset Form ---
  return (
    <div className="min-h-screen bg-black text-white">
      <header className="sticky top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-md border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <Link to="/" className="flex items-center">
              <span className="text-xl sm:text-2xl font-bold">
                <span className="text-yellow-400">Nutri</span>
                <span className="text-white">Bot</span>
              </span>
            </Link>
            <Link
              to="/login"
              className="flex items-center gap-2 text-xs sm:text-sm text-gray-300 hover:text-yellow-400 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              <span>{t('auth.resetPassword.backToLogin')}</span>
            </Link>
          </div>
        </div>
      </header>

      <div className="min-h-[calc(100vh-56px)] sm:min-h-[calc(100vh-64px)] flex items-center justify-center px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-md w-full">

          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-yellow-500/20 rounded-2xl flex items-center justify-center">
              <i className="ri-shield-keyhole-line text-yellow-400 text-3xl"></i>
            </div>
          </div>

          {/* Headline */}
          <div className="text-center mb-6 sm:mb-8">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent">
              {t('auth.resetPassword.title')}
            </h1>
            <p className="text-lg sm:text-xl text-gray-300">
              {t('auth.resetPassword.subtitle')}
            </p>
          </div>

          <div className="bg-gray-900 rounded-2xl p-6 sm:p-8 border border-gray-800">
            <form onSubmit={resetPassword} className="space-y-5">

              {/* Password Requirements */}
              {newPassword.length > 0 && (
                <div className="bg-gray-800/60 rounded-lg px-4 py-3 space-y-2">
                  {[
                    { met: hasLength, label: t('auth.resetPassword.reqLength') },
                    { met: hasUppercase, label: t('auth.resetPassword.reqUppercase') },
                    { met: hasNumber, label: t('auth.resetPassword.reqNumber') },
                    ...(confirmPassword.length > 0
                      ? [{ met: passwordsMatch, label: t('auth.resetPassword.reqMatch') }]
                      : []),
                  ].map(({ met, label }, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <i className={`text-base flex-shrink-0 ${met ? 'ri-checkbox-circle-fill text-green-400' : 'ri-close-circle-line text-gray-600'}`}></i>
                      <span className={`text-xs ${met ? 'text-green-400' : 'text-gray-500'}`}>
                        {label}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* New Password */}
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-300 mb-2">
                  {t('auth.resetPassword.newPasswordLabel')}
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className={`w-full px-4 py-3 pr-12 bg-gray-800 text-white rounded-lg border ${
                      errors.newPassword ? 'border-red-500' : 'border-gray-700'
                    } focus:border-yellow-400 focus:outline-none transition`}
                    placeholder={t('auth.resetPassword.newPasswordPlaceholder')}
                    disabled={isResetting}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-yellow-400 transition cursor-pointer"
                  >
                    <i className={showNewPassword ? 'ri-eye-off-line text-lg' : 'ri-eye-line text-lg'}></i>
                  </button>
                </div>
                {errors.newPassword && (
                  <p className="mt-1 text-sm text-red-400">{errors.newPassword}</p>
                )}

                {/* Strength Bar */}
                {newPassword.length > 0 && (
                  <div className="mt-2">
                    <div className="flex gap-1">
                      {[1, 2, 3].map((level) => (
                        <div
                          key={level}
                          className={`h-1 flex-1 rounded transition-ball duration-300 ${
                            strengthScore >= level ? strengthColor : 'bg-gray-700'
                          }`}
                        />
                      ))}
                    </div>
                    {strengthLabel && (
                      <p className={`text-xs mt-1 ${strengthTextColor}`}>{strengthLabel}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-2">
                  {t('auth.resetPassword.confirmPasswordLabel')}
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={`w-full px-4 py-3 pr-12 bg-gray-800 text-white rounded-lg border ${
                      errors.confirmPassword ? 'border-red-500' : 'border-gray-700'
                    } focus:border-yellow-400 focus:outline-none transition`}
                    placeholder={t('auth.resetPassword.confirmPasswordPlaceholder')}
                    disabled={isResetting}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-yellow-400 transition cursor-pointer"
                  >
                    <i className={showConfirmPassword ? 'ri-eye-off-line text-lg' : 'ri-eye-line text-lg'}></i>
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-400">{errors.confirmPassword}</p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isResetting || !newPassword || !confirmPassword || !passwordsMatch || strengthScore < 3}
                className="w-full py-3 sm:py-4 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold rounded-xl hover:from-yellow-500 hover:to-yellow-700 transition-ball duration-200 cursor-pointer shadow-lg hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {isResetting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin text-lg"></i>
                    {t('auth.resetPassword.resetting')}
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-lock-unlock-line text-lg"></i>
                    {t('auth.resetPassword.submitButton')}
                  </span>
                )}
              </button>
            </form>

            {/* Security Note */}
            <div className="mt-6 flex items-start gap-3 bg-gray-800/50 rounded-lg px-4 py-3">
              <i className="ri-information-line text-blue-400 text-lg flex-shrink-0 mt-0.5"></i>
              <p className="text-xs text-gray-400 leading-relaxed">
                {t('auth.resetPassword.securityNote')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}